import React, { useEffect, useState } from 'react';
import { ICountryDetail } from '../../../types/countries';
import NudosButton from '../../../components/NudosComponents/NudosButton/NudosButton';
import NudosPhoneInput from '../../../components/NudosComponents/NudosPhoneInput/NudosPhoneInput';
import NudosTextInput from '../../../components/NudosComponents/NudosTextInput/NudosTextInput';
import NudosStaticAlertInComponent from '../../../components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import { getNudosData } from '../../../utils/getNudosData';
import { createOrUpdateUserAdditionalData, updatePersonalData } from '../../../services/user.service';
import { CALENDLY_LINK_TYPE_ID, IPhotoProfile, IUserAdditionalData } from '../../../types/configuration.types';
import { displayErrorNotification, displaySuccessNotification } from '../../../utils/displayNudosStandardNotifications';
import BordPhotoUploader from '../../../components/NudosComponents/BordPhotoUploader/BordPhotoUploader';
import { countriesList as getCountriesList } from '../../../services/countriesList';
import { validateImg } from '../../../utils/validateFiles';
import useAccountState from '../../../state/account.state';
import { getLoggedUserPermissions } from '../../../services/configuration.service';
import './UserAccountDetails.scss';

const UserAccountDetails: React.FC = () => {
  const userData = getNudosData()?.userData;
  const { userStateData, setUserStateData } = useAccountState();

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [calendlyLink, setCalendlyLink] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [selectedCountry, setSelectedCountry] = useState<ICountryDetail>();
  const [userAdditionalData, setUserAdditionalData] = useState<IUserAdditionalData[]>();
  const [countriesList, setCountriesList] = useState<ICountryDetail[]>();
  const [photoProfile, setPhotoProfile] = useState<IPhotoProfile>();

  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    calendlyLink?: string;
    phoneNumber?: string;
    selectedCountry?: ICountryDetail;
    photoProfile?: IPhotoProfile;
  }>();

  const showSkeletonLoader = loadingData || !isInitialDataLoaded;

  const setInitialValues = () => {
    if (!userData || loadingData) return;

    const myDefaultCountry = countriesList?.find(
      country => country.phoneCode?.toString() === userData?.dataPhone?.phoneCode
    );

    const newInitialData = {
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      email: userData?.email,
      calendlyLink: userStateData.calendarLink,
      phoneNumber: userData?.dataPhone?.phone || '',
      selectedCountry: myDefaultCountry,
      photoProfile: {
        url: userData?.photoProfile
      }
    };

    setFirstName(newInitialData.firstName);
    setLastName(newInitialData.lastName);
    setEmail(newInitialData.email);
    setCalendlyLink(newInitialData.calendlyLink);
    setPhoneNumber(newInitialData.phoneNumber);
    setSelectedCountry(newInitialData.selectedCountry);
    setInitialData(newInitialData);
    setIsInitialDataLoaded(true);
    setPhotoProfile(newInitialData.photoProfile);
    setUserAdditionalData(userAdditionalData);
  };

  const valuesHadChanged = (): boolean => {
    if (!initialData || !isInitialDataLoaded) return false;
    if (!firstName || !lastName || !phoneNumber || !selectedCountry?.id) return false;
    return (
      firstName !== initialData.firstName ||
      lastName !== initialData.lastName ||
      phoneNumber !== initialData.phoneNumber ||
      calendlyLink !== initialData.calendlyLink ||
      selectedCountry?.id !== initialData.selectedCountry?.id ||
      photoProfile?.url !== initialData.photoProfile?.url ||
      !!photoProfile?.file
    );
  };

  const updateUserAdditionalData = async () => {
    if (!userData?.id) return;
    const body = {
      userId: userData.id,
      additionalDataTypeId: CALENDLY_LINK_TYPE_ID,
      value: calendlyLink
    };
    if (calendlyLink === initialData?.calendlyLink) return;
    await createOrUpdateUserAdditionalData(body);
  };

  const updatePersonalInfo = async () => {
    if (!userData?.id) return;
    const formData = {
      firstName,
      lastName,
      countryPhoneId: selectedCountry?.id?.toString(),
      phone: phoneNumber,
      email
    };
    await updatePersonalData(Number(userData?.id), formData, photoProfile?.file);
  };

  const handleSave = async () => {
    setLoadingSave(true);
    try {
      await Promise.all([updateUserAdditionalData(), updatePersonalInfo()]);
      setInitialData({
        firstName,
        lastName,
        email,
        calendlyLink,
        phoneNumber,
        selectedCountry,
        photoProfile: {
          url: photoProfile?.url
        }
      });

      setUserStateData({
        profilePhoto: photoProfile?.url || '',
        firstName: firstName || '',
        lastName: lastName || '',
        phoneNumber: phoneNumber || '',
        calendarLink: calendlyLink || ''
      });

      if (userData?.id) {
        const updatedUserData = await getLoggedUserPermissions(userData.uid);
        localStorage.setItem('userNudosData', JSON.stringify(updatedUserData));
      }

      displaySuccessNotification({ customJSXMessage: <>Información actualizada con éxito</> });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingSave(false);
    }
  };

  const loadInitialData = async () => {
    if (!userData) return;
    try {
      setLoadingData(true);
      const countries = await getCountriesList();
      setCountriesList(countries);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingData(false);
    }
  };

  const onChangeFile = (file: File) => {
    const validFile = validateImg(file);
    if (validFile) {
      setPhotoProfile({
        file,
        url: validFile?.temporalUrl
      });
    }
  };

  const getAlertMessage = (): string => {
    const userHasCompleteInformation = !!(
      userStateData.firstName &&
      userStateData.lastName &&
      email &&
      userStateData.phoneNumber &&
      userStateData.profilePhoto &&
      userStateData.calendarLink
    );
    return userHasCompleteInformation
      ? 'Si eres KAM, tu información es visible para tus clientes asignados en Dash. Recuerda mantenerla actualizada para una mejor comunicación'
      : 'Si eres KAM, esta información la compartiremos en Dash a tus clientes asignados.';
  };

  useEffect(() => {
    if (!loadingData) setInitialValues();
  }, [loadingData, countriesList, userAdditionalData]);

  useEffect(() => {
    loadInitialData();
  }, []);

  return (
    <div className="userAccountDetails">
      <div className="title">Información personal</div>
      <div className="profileSection">
        <BordPhotoUploader
          bordPhotoSize="s40"
          selectedImageUrl={photoProfile?.url || ''}
          uploadedFileHandler={onChangeFile}
          showSkeletonLoader={showSkeletonLoader}
        />
        <div className="imageHelp">
          <div className="imageTitle">Imagen de perfil</div>
          <div className="imageSubtext">Formatos permitidos: jpg o png, máximo 2MB</div>
        </div>
      </div>

      <div className="form">
        <NudosTextInput
          placeholder="Agrega tu nombre"
          label="Nombre"
          isFilled={!!firstName}
          defaultValue={firstName}
          handleChange={setFirstName}
          componentSize="medium"
          showSkeletonLoader={showSkeletonLoader}
        />
        <NudosTextInput
          placeholder="Agrega tu apellido"
          label="Apellido"
          isFilled={!!lastName}
          defaultValue={lastName}
          handleChange={setLastName}
          componentSize="medium"
          showSkeletonLoader={showSkeletonLoader}
        />
        {!loadingData && (
          <NudosStaticAlertInComponent
            customClassname="customNudosStaticAlertInComponent"
            type="information"
            alertOptions={{
              title: getAlertMessage()
            }}
          />
        )}
      </div>

      <div className="form">
        <NudosPhoneInput
          placeholder="Agrega tu número de teléfono"
          label="Teléfono"
          componentSize="medium"
          handlePhoneChange={setPhoneNumber}
          handleCountryPhoneChange={setSelectedCountry}
          countriesList={countriesList || []}
          defaultPhoneNumber={phoneNumber}
          defaultCountryData={selectedCountry}
          showSkeletonLoader={showSkeletonLoader}
        />
        <NudosTextInput
          placeholder="Agrega tu correo"
          label="Correo"
          isFilled={!!email}
          defaultValue={email}
          handleChange={setEmail}
          componentSize="medium"
          autoComplete="email"
          isDeactivated
          showSkeletonLoader={showSkeletonLoader}
        />
        <NudosTextInput
          placeholder="Agrega el enlace"
          label="Link de Calendly"
          isFilled={!!calendlyLink}
          defaultValue={calendlyLink}
          handleChange={setCalendlyLink}
          componentSize="medium"
          showSkeletonLoader={showSkeletonLoader}
        />
      </div>

      <NudosButton
        buttonText="Guardar"
        handleClick={handleSave}
        colorPalette="primary"
        componentSize="medium"
        customClassName="saveButton"
        isButtonLoading={loadingSave}
        isButtonDisabled={!valuesHadChanged() || showSkeletonLoader}
      />
    </div>
  );
};

export default UserAccountDetails;
